import { useCallback, useRef, useState } from "react"
import { Tabs } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import {
    ResourceFilterElement,
    ResourceFilterElementValue,
    ResourceFiltersSelectedItem,
} from "../../../../../shared/components/createOrder/createOrderTypes"
import { AddFiltersButton } from "../../../../../shared/components/createOrder/components/resource/resourceFilters/AddFiltersButton"
import { FilterChip } from "../../../../../shared/components/createOrder/components/resource/resourceFilters/FilterChip"

import { relocalizeSelectedFilters } from "./filterStorage"

const useStyles = makeStyles()(() => ({
    chips: {
        minWidth: 0,
        "&.MuiTabs-root": {
            minHeight: "2.5rem",
            height: "2.5rem",
        },
        "& .MuiTabs-scroller": {
            display: "flex",
            alignItems: "center",
        },
        "& .MuiTabs-flexContainer": {
            gap: ".75rem",
            alignItems: "center",
        },
        "& .MuiTabs-indicator": {
            display: "none",
        },
    },
}))

function getCommittedFilters(list: readonly ResourceFiltersSelectedItem[]): ResourceFiltersSelectedItem[] {
    return list
        .filter((filter) => filter.values.length > 0)
        .map((filter) => ({ ...filter, values: [...filter.values] }))
}

export function useMeetingFilter(
    availableFilters: ResourceFilterElement[],
    initialFilters: ResourceFiltersSelectedItem[],
    onChange: (filters: ResourceFiltersSelectedItem[]) => void,
) {
    const [activeFilter, setActiveFilter] = useState<string | null>(null)
    const [chipFilters, setChipFilters] = useState<ResourceFiltersSelectedItem[]>(() =>
        relocalizeSelectedFilters(initialFilters, availableFilters),
    )
    const chipFiltersRef = useRef<ResourceFiltersSelectedItem[]>(chipFilters)

    const commit = useCallback(
        (next: ResourceFiltersSelectedItem[]) => {
            chipFiltersRef.current = next
            setChipFilters(next)
            onChange(getCommittedFilters(next))
        },
        [onChange],
    )

    const handleAddFilter = (identifier: string) => {
        const prev = chipFiltersRef.current
        if (prev.some((filter) => filter.identifier === identifier)) {
            commit(prev.filter((filter) => filter.identifier !== identifier))
            return
        }
        const elementValues = availableFilters.find((filter) => filter.identifier === identifier)?.values ?? []
        // Boolean filter (one value tagged with its own identifier) applies at once; multiselect opens the menu to pick.
        const isBoolean = elementValues.length === 1 && elementValues[0].value === identifier
        if (isBoolean) {
            commit([...prev, { identifier, values: [...elementValues] }])
            return
        }
        commit([...prev, { identifier, values: [] }])
        setActiveFilter(identifier)
    }

    const handleUpdateValues = (identifier: string, values: ResourceFilterElementValue[]) => {
        commit(
            chipFiltersRef.current.map((filter) => (filter.identifier === identifier ? { ...filter, values } : filter)),
        )
    }

    const handleRemove = (identifier: string) => {
        commit(chipFiltersRef.current.filter((filter) => filter.identifier !== identifier))
    }

    return {
        availableFilters,
        chipFilters,
        activeFilter,
        setActiveFilter,
        handleAddFilter,
        handleUpdateValues,
        handleRemove,
    }
}

export type MeetingFilterController = ReturnType<typeof useMeetingFilter>

export function MeetingFilterButton({ controller }: Readonly<{ controller: MeetingFilterController }>) {
    return (
        <AddFiltersButton
            filters={controller.chipFilters}
            resourceFiltersResult={controller.availableFilters}
            onAddFilter={controller.handleAddFilter}
        />
    )
}

export function MeetingFilterChips({ controller }: Readonly<{ controller: MeetingFilterController }>) {
    const { classes } = useStyles()

    return (
        <Tabs
            value={false}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            className={classes.chips}
        >
            {controller.chipFilters.map((filter) => (
                <FilterChip
                    key={filter.identifier}
                    filterType={filter.identifier}
                    isRemovable={filter.isRemovable}
                    onRemove={() => controller.handleRemove(filter.identifier)}
                    resourceFiltersResult={controller.availableFilters}
                    isActive={controller.activeFilter === filter.identifier}
                    setActiveFilter={controller.setActiveFilter}
                    initialValues={filter.values}
                    onSave={(values) => controller.handleUpdateValues(filter.identifier, values)}
                />
            ))}
        </Tabs>
    )
}
